<template>
	<div
		id="author-page"
		class="custom-link"
	>
		<div class="author-card-container">
			<div class="author-card">
				<div class="author-image">
					<img
						v-if="authorImage"
						:src="authorImage"
						alt="Author Headshot"
					/>
					<img
						v-else
						src="https://secure.gravatar.com/avatar/04554fdd4796f793a329464e8d983881?s=120&d=mm&r=g"
						alt="Author Headshot"
					/>
				</div>
				<div class="author-information">
					<div class="author-details">
						<span class="author-name white title">{{ authorName }}</span>
						<span
							class="author-title subtitle"
							v-html="anyObject.position != '' ? anyObject.position : subtitle"
						/>
					</div>

					<hr class="separator mb-3 border-white opacity-25" />
					<div class="author-proficiency">
						<div class="author-expertise">
							<span class="author-proficiency-title gold">Expertise</span>
							<div
								class="author-expertise-description white"
								v-html="expertise"
							></div>
						</div>
						<div class="author-education">
							<span class="author-proficiency-title gold">Education</span>
							<span
								class="author-expertise-description white"
								v-html="education"
							></span>
						</div>
					</div>

					<div class="author-socialmedia">
						<a
							v-if="authorEmail !== ''"
							:href="'mailto:' + authorEmail"
							class="social-icon email-social"
							target="_blank"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="5"
								height="5"
								viewBox="0 0 16 16"
							>
								<path
									fill="#fff"
									d="M15,1H1C0.448,1,0,1.447,0,2v12c0,0.553,0.448,1,1,1h14c0.552,0,1-0.447,1-1V2C16,1.447,15.552,1,15,1z 
                              M14,13H2V6.723l5.504,3.145c0.308,0.176,0.685,0.176,0.992,0L14,6.723V13z M14,4.42L8,7.849L2,4.42V3h12V4.42z"
								/>
							</svg>
						</a>

						<template
							v-if="
								sections.length > 0 &&
								sections.filter((s) => ['Social'].includes(s.name)).length > 0
							"
						>
							<template v-for="section in sections">
								<template v-if="section.name === 'Social'">
									<NuxtLink
										v-for="(item, index) in section.values"
										:key="index"
										:to="item.value"
										:href="item.value"
										class="social-icon"
										target="_blank"
									>
										<img
											class="w-6 h-6 object-cover"
											:src="`/images/icons/${
												item.label === 'profitly'
													? 'profitly.png'
													: item.label === 'muckrack'
													? 'muckrack.png'
													: `${item.label.toLowerCase()}.svg`
											}`"
										/>
									</NuxtLink>
								</template>
							</template>
						</template>
					</div>
				</div>
			</div>
		</div>
		<div
			id="author-content"
			class="xl:container gap-[80px] mx-auto"
		>
			<BlogContainer class="custom-blog-container">
				<template #aside>
					<AizenSidebarAd class="lg:sticky lg:top-[92px]" />
				</template>
				<template #main>
					<template
						v-if="
							sections.length > 0 &&
							sections.filter((s) => ['Video', 'Wistia'].includes(s.name)).length > 0
						"
					>
						<template v-for="section in sections">
							<template v-if="section.name && ['Video'].includes(section.name)">
								<div
									v-for="(item, index) in section.values"
									:key="index"
									id="youtube-player"
									ref="videoContainer"
									class="w-full max-w-[700px] aspect-video"
									:data-url="item.value"
								></div>
							</template>
							<template v-if="section.name && ['Wistia'].includes(section.name)">
								<div
									v-for="(item, index) in section.values"
									:key="index"
									id="wistia-player"
									ref="videoContainer"
									class="w-full max-w-[700px] aspect-video mb-3"
								>
									<iframe
										:src="item.value"
										title="Millionaire Publishing - Challenge 17 - Pitch Update Free - 16-9 - v1 (1) Video"
										allow="autoplay; fullscreen"
										allowtransparency="true"
										frameborder="0"
										scrolling="no"
										class="wistia_embed"
										name="wistia_embed"
										msallowfullscreen
										width="100%"
										height="100%"
									/>
								</div>
							</template>
						</template>
					</template>
					<div
						v-if="highlights"
						class="author-body mb-8"
					>
						<h2 class="author-content-title !pb-3">Highlights</h2>

						<div v-html="highlights"></div>
					</div>

					<div
						v-if="experience"
						class="author-body mb-8"
					>
						<h2 class="author-content-title !pb-3">Experience</h2>
						<div
							v-html="experience"
							class="author-experience-paragraph"
						></div>
					</div>

					<div
						v-if="
							sections.length > 0 &&
							sections.filter((s) =>
								['Interviews', 'Featured on', 'Host', 'Author', 'Founder'].includes(
									s.name
								)
							).length > 0
						"
					>
						<h2 class="author-content-title pb-0">Publications and Interviews</h2>
						<template v-for="section in sections">
							<div
								v-if="
									section.name &&
									PUBLICATION_AND_INTERVIEWS_LABELS.includes(section.name)
								"
								:key="section.name"
								class="flex flex-col mb-6"
							>
								<h3 class="text-lg font-semibold !mb-2">{{ section.name }}</h3>

								<div class="grid lg:grid-cols-2 row-gap-2">
									<span
										v-for="(item, index) in section.values"
										:key="index"
									>
										<NuxtLink
											:to="item.value"
											target="_blank"
											class="text-primary-500 hover:text-primary-700"
										>
											{{ item.label }}
										</NuxtLink>
									</span>
								</div>
							</div>
						</template>
					</div>
				</template>
			</BlogContainer>
		</div>
		<Row id="custom-padding">
			<div
				v-if="authorName === 'Tim Sykes' || authorName === 'Tim Bot'"
				class="author-latest-posts"
			>
				<h2 class="author-content-title-post">More from {{ authorName }}</h2>
				<Divider />
				<div class="author-post-container">
					<AuthorPostTile
						v-for="post in posts.slice(0, visiblePostCount)"
						:key="post.id"
						:content="post.content"
						:source-url="post.featuredImage?.node?.sourceUrl || defaultImage"
						:title="post.title"
						:slug="post.slug"
						:author="authorName"
						:data-page="post.page"
					/>
				</div>
				<div class="view-more-button-container">
					<button
						v-show="shouldShowViewMoreButton"
						class="view-more-button"
						type="button"
						@click="loadMorePosts"
					>
						View more
					</button>
				</div>
			</div>
		</Row>
	</div>
</template>
<script setup>
import { ref, computed, onBeforeMount } from "vue";
import { timSykesPosts, timBotPosts } from "~/utils/postsData.js";

const props = defineProps({
	authorName: { type: String, required: true },
	authorTitle: { type: String, default: "" },
	authorExpertise: { type: String, default: "" },
	authorEducation: { type: String, default: "" },
	authorHighlights: { type: String, default: "" },
	experience: { type: String, default: "" },
	authorEducationContent: { type: String, default: "" },
	authorEmail: { type: String, default: "" },
	authorImage: { type: String, default: "" },
	custom_sections: { type: String, default: "" },
	anyObject: { type: Object, default: () => {} }
});

const subtitle = ref("");
const posts = ref([]);
const visiblePostCount = ref(8);
const route = useRoute();
const $config = useRuntimeConfig();
const defaultImage = "/images/default_blog_image.webp";
const loading = ref(true);
const videoContainer = ref(null);
const shouldShowViewMoreButton = computed(() => {
	return visiblePostCount.value < posts.value.length;
});

const PUBLICATION_AND_INTERVIEWS_LABELS = [
	"Interviews",
	"Featured on",
	"Host",
	"Author",
	"Articles",
	"Speaker",
	"Founder",
	"Testimonials"
];

const loadMorePosts = () => {
	visiblePostCount.value += 8;
};

const fetchPostByAuthor = async ($author) => {
	try {
		// Call data saved from list.
		if (route.fullPath === "/blog/author/tim-bot/") {
			posts.value = timBotPosts;
		} else if (route.fullPath === "/blog/author/tim-sykes/") {
			posts.value = timSykesPosts;
		}
		loading.value = true;
		// Fetch the newest posts.
		const response = await $fetch(`${$config.public.hostname}graphql`, {
			method: "post",
			body: JSON.stringify({
				query: `
						query newQuery {
							posts(where: {authorName: "${$author}"}, first: 100) {
								nodes {
									title(format: RENDERED)
									uri
									content(format: RENDERED)
									id
									slug
									featuredImage {
										node {
											sourceUrl
										}
									}
								}
							}
						}
					`
			})
		});
		posts.value = response.data.posts.nodes;
	} catch (error) {
		return [];
	} finally {
		loading.value = false;
	}
};

function onYouTubeIframeAPIReady(id) {
	try {
		if (
			!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')
		) {
			const script = document.createElement("script");
			script.setAttribute("src", "https://www.youtube.com/iframe_api");
			document.head.appendChild(script);
		}

		setTimeout(() => {
			const _player = new YT.Player("youtube-player", {
				videoId: id, // Replace with your video's ID
				playerVars: {
					autoplay: true, // Autoplay is turned off
					controls: 1, // Show video controls
					modestbranding: 1, // Hide the YouTube logo
					rel: 0 // Don't show related videos
				}
			});

			_player.playVideo();
		}, 2000);
	} catch (e) {
		// eslint-disable-next-line no-console
		console.log("Error in Video injection: ", e);
	}
}
const callFetchPost = () => {
	if (route.fullPath === "/blog/author/tim-bot/") {
		fetchPostByAuthor("Tim-Bot");
	} else if (route.fullPath === "/blog/author/tim-sykes/") {
		fetchPostByAuthor("TimothySykes");
	}
};

onBeforeMount(() => {
	if (props.experience) {
		// eslint-disable-next-line vue/no-mutating-props
		props.experience = "";
	}
	if (props.authorTitle) {
		subtitle.value = props.authorTitle;
		callFetchPost();
	}
});

watch(videoContainer, (video) => {
	if (process.client && video && video.length > 0) {
		const id = video[0].getAttribute("data-url").split("v=")[1];
		onYouTubeIframeAPIReady(id);
	}
});

const experience = computed(() => {
	if (props.anyObject.experience) {
		return props.anyObject.experience.replace(/"/g, "");
	}

	return "";
});
const expertise = computed(() => {
	if (props.authorExpertise) {
		return props.authorExpertise.replace(/"/g, "");
	}

	return "";
});
const education = computed(() => {
	if (props.authorEducation) {
		return props.authorEducation.replace(/"/g, "");
	}

	return "";
});

const highlights = computed(() => {
	if (props.authorHighlights) {
		return props.authorHighlights.replace(/"/g, "");
	}
	return "";
});
const sections = computed(() => {
	if (props.custom_sections) {
		return JSON.parse(props.custom_sections);
	}
	return [];
});
</script>
<style lang="scss">
@import "@assets/utils.scss";

#author-page {
	#custom-padding {
		padding: 0;
	}
	.author-card-container {
		display: flex;
		justify-content: center;
		align-items: center;
		padding: 32px 382px;
		background: #1e3a8a;
	}
	.title {
		font-weight: 700;
		font-size: 24px;
	}
	.subtitle {
		font-weight: 400;
		font-size: 16px;
		color: white !important;
	}

	.author-card {
		display: grid;
		width: 920px;
		grid-template-columns: auto 1fr;
		grid-gap: 20px;
		grid-template-rows: auto auto;
		row-gap: 16px;
		align-items: center;
	}
	.author-name {
		display: block;
		font-size: 24px;
		font-weight: 700;
		color: white;
	}
	.author-title {
		display: block;
		font-size: 16px;
		font-weight: 400;
		color: #fac515;
	}
	.white {
		color: white;
	}

	.gold {
		color: #fac515;
	}

	:deep(.blueLink) {
		color: #3fa9f5 !important;
	}

	:deep(.blueLink:hover) {
		color: #f78400;
		text-decoration: underline;
		text-decoration-color: white;
	}

	:deep(.subtitle a) {
		color: #3fa9f5;
		text-decoration: none;
		text-decoration-color: white;
	}

	.author-post-container {
		display: grid;
		grid-template-columns: repeat(4, minmax(0, 1fr));
		width: 1200px;
		gap: 32px;
		margin-top: 16px;
		margin-bottom: 20px;
	}
	.author-proficiency-title {
		font-size: 18px;
		font-weight: 700;
	}
	.author-image {
		display: inline-block;
		border-radius: 200px;
		border: 5px solid #93c5fd;
		overflow: hidden;
		width: 160px;
		height: 160px;
		margin-right: 20px;
		align-self: start;
	}

	.author-image img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.author-information {
		flex: 1;
		display: flex;
		flex-direction: column;
	}

	:deep(.separator) {
		color: white;
		box-shadow: inset 0 0.25px 0 0 #fff;
		margin: 16px 0;
	}

	.post-separator {
		margin-top: 52px;
		margin-bottom: 32px;
	}

	.author-expertise-description {
		padding: 8px 0;
		font-size: 18px;
	}

	.author-expertise-description,
	.author-education-description,
	.author-details,
	.author-post-description span {
		display: block;
	}

	.author-details {
		width: 100%;
		margin-bottom: 16px;
	}

	.view-more-button-container {
		display: flex;
		justify-content: center;
		align-items: center;
		text-align: center;
		padding: 20px;
	}

	.view-more-button {
		-webkit-box-align: center;
		align-items: center;
		cursor: pointer;
		display: inline-flex;
		-webkit-box-pack: center;
		justify-content: center;
		pointer-events: auto;
		position: relative;
		text-align: center;
		transition: all 175ms ease 0s;
		user-select: none;
		white-space: nowrap;
		background: #1e3a8a;
		box-sizing: border-box;
		font-size: 16px;
		font-family: Rubik, sans-serif;
		font-weight: 500;
		line-height: 1;
		padding: 12px 20px;
		height: min-content;
		border-radius: 8px;
		border: 0px solid transparent;
		box-shadow: rgb(28, 80, 158) 0px -1px 0px 0px inset,
			rgba(0, 0, 0, 0.12) 0px 2px 3px 0px;
		color: rgb(255, 255, 255);
		min-height: 48px;
		min-width: 48px;
		margin: initial;
	}

	/*Social media Icos*/
	.social-icon {
		display: flex;
		height: 32px;
		width: 32px;

		border-radius: 50%;
		background-color: #93c5fd;
		text-align: center;
		overflow: hidden;
		transition: background-color 0.3s ease;
		align-items: center;
		justify-content: center;

		@include breakpoint(lg) {
			width: 40px;
			height: 40px;
			line-height: 40px;
		}
	}

	.social-icon svg {
		display: block;
		margin: 0 auto;
		width: 60%;
		height: 60%;
		object-fit: contain;
		transition: transform 0.3s ease;
	}

	.social-icon:hover {
		background-color: #759cc4;
	}

	.social-icon:hover img {
		transform: scale(1.1);
	}

	.author-highlights,
	.author-experience,
	.author-education {
		grid-column: 2;
	}

	.author-content-title {
		font-size: 38px;
		font-weight: 700;
		padding: 0px 0px 32px 0px;
	}

	.author-highlight-item,
	.author-experience-paragraph,
	.author-education-paragraph {
		font-weight: 400;
		font-size: 18px;
		text-align: justify;
		margin-bottom: 24px;
	}

	.author-highlight-list {
		list-style-type: none;
		padding-left: 8px;
	}

	.author-highlight-list li {
		position: relative;
		padding-left: 16px;
		line-height: 1.4;
	}

	.author-highlight-list li::before {
		content: "";
		position: absolute;
		left: 0;
		top: 8px;
		width: 8px;
		height: 8px;
		background-color: #3094fb;
	}

	.author-latest-posts {
		grid-column: 2 / span 1;
		grid-row: 4;
		margin-left: 100px;
		margin-right: 100px;
		margin-bottom: 60px;
	}

	.author-content-title-post {
		font-size: 38px;
		font-weight: 700;
		padding-bottom: 0px;
	}

	/* Mobile Styles */
	@media (max-width: 1700px) {
		.author-post-container {
			grid-template-columns: repeat(3, minmax(0, 1fr));
			width: 100%;
		}
	}
	@media (max-width: 1200px) {
		.author-post-container {
			grid-template-columns: repeat(2, minmax(0, 1fr));
			width: 100%;
		}
	}
	@media (max-width: 700px) {
		.author-post-container {
			display: flex !important;
			flex-direction: column !important;
			width: 100%;
		}
	}
	@media (max-width: 768px) {
		.author-card-container {
			padding: 32px 20px;
		}

		.author-card {
			grid-template-columns: auto;
			grid-template-rows: auto auto auto;
			justify-items: center;
			text-align: center;
		}

		.author-image {
			margin-right: 0;
		}

		.author-information {
			margin-top: 20px;
		}

		.author-content-title {
			font-size: 28px;
		}

		.author-content-title-post {
			margin-left: 20px !important;
			font-size: 28px !important;
		}
		.author-highlight-list {
			padding-left: 0;
			margin-left: 0;
		}

		.author-latest-posts {
			margin: 0px;
		}

		.author-post-container {
			display: block;
			gap: 24px;
			padding: 0px 20px 10px 20px;
			margin-top: 0px;
			width: 100%;
		}
		.author-post {
			grid-template-columns: 1fr;
			flex: 1;
		}
		.author-post-image {
			grid-row: auto;
		}

		.author-post-image-image {
			width: 100%;
			height: auto;
		}

		.author-post-description {
			margin-top: 10px;
			padding-left: 0;
		}

		.author-post-link {
			display: block;
			margin-top: 10px;
			text-align: center;
		}

		.author-content-title-post {
			font-size: 18px;
		}
	}
	@media (min-width: 769px) and (max-width: 1024px) {
		.author-latest-posts {
			margin-left: 20px !important;
			margin-right: 20px !important;
		}

		.author-side-box {
			grid-column: 2 / span 1;
			grid-row: 4 / span 2;
			margin-left: 0 !important;
			width: 100% !important;
			height: auto !important;
			padding: 20px !important;
			box-shadow: 0px 7px 20px 0px #0000001a !important;
		}

		.author-card-container {
			padding: 32px 20px;
		}

		.author-card {
			grid-template-columns: auto;
			grid-template-rows: auto auto auto;
			justify-items: center;
			text-align: center;
		}
	}
	@media (min-width: 1025px) and (max-width: 1400px) {
		.author-latest-posts {
			margin-left: 20px !important;
			margin-right: 20px !important;
		}

		.author-side-box {
			grid-column: 2 / span 1;
			grid-row: 4 / span 2;
			margin-left: 0 !important;
			width: 100% !important;
			height: auto !important;
			padding: 20px !important;
			box-shadow: 0px 7px 20px 0px #0000001a !important;
		}

		.author-post {
			flex: 1;
			margin: 0 10px;
		}

		.author-card-container {
			padding: 32px 20px;
		}

		.author-card {
			grid-template-columns: auto;
			grid-template-rows: auto auto auto;
			justify-items: center;
			text-align: center;
		}
	}

	#author-page .blog-container {
		overflow-x: visible !important;
	}

	li {
		margin-bottom: 1.15rem;
		position: relative;
		padding-left: 16px;
		font-size: 1.1rem;
		&::before {
			content: "";
			position: absolute;
			left: 0;
			top: 0.5rem;
			width: 8px;
			height: 8px;
			background-color: #3094fb;
		}
	}

	// #author-content {

	.author-highlights a {
		&::after {
			content: "";
			background-image: url("/images/icons/external-link.svg");
			width: 14px;
			height: 14px;
			display: inline-block;
			background-size: contain;
			margin-left: 2px;
		}

		&:hover {
			background-color: #eab308;
			color: #1e3a8a;
			text-decoration: underline;
			&::after {
				background-image: url("/images/icons/external-link-hover.svg");
			}
		}
	}
	// }

	.author-socialmedia {
		margin-top: 20px;
		display: flex;
		gap: 12px;
		margin: auto;
		@include breakpoint(lg) {
			margin-left: 0;
			margin-right: 0;
		}
	}

	#youtube-player {
		min-height: 300px;
		max-height: 380px;
		margin-bottom: 32px;
		@include breakpoint(lg) {
			min-height: 380px;
		}
	}
}
</style>
